import type { ComponentType } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { can } from '../auth/types';
import type { AuthSession } from '../auth/types';
import { useRouter } from './router';
import { NotificationsBell } from '../screens/notifications/NotificationsBell';
import { LANGUAGES } from '../i18n/catalogs';
import { APP_VERSION } from '../version.generated';
import { NAV_ROUTES, matchRoute } from './routes';
import type { AppRoute, ScreenProps } from './routes';

/**
 * Screens are discovered, not imported one by one: a later ticket drops
 * `apps/web/src/screens/<screen>/index.tsx` and the route starts rendering it.
 * Until then the route renders its placeholder.
 */
const discovered = import.meta.glob<{ default: ComponentType<ScreenProps> }>('../screens/*/index.tsx', {
  eager: true,
});

const SCREENS: Record<string, ComponentType<ScreenProps>> = {};
for (const [file, module] of Object.entries(discovered)) {
  const id = file.split('/')[2];
  if (id !== undefined && module.default !== undefined) {
    SCREENS[id] = module.default;
  }
}

type NavGroup = { titleKey: string; paths: readonly string[] };

const NAV_ICONS: Record<string, string> = {
  '/': 'M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z',
  '/accounts': 'M4 6h16M4 12h16M4 18h10',
  '/reports/trial-balance': 'M5 20V10M12 20V4M19 20v-7',
  '/closing': 'M12 3a9 9 0 1 0 9 9M12 7v5l3 2',
  '/documents': 'M6 3h8l4 4v14H6zM14 3v4h4',
  '/journal': 'M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3zM8 8h6',
  '/settings': 'M4 7h16M4 12h16M4 17h16M9 7v0M15 12v0M7 17v0',
  '/notifications': 'M6 9a6 6 0 1 1 12 0c0 4 2 5 2 5H4s2-1 2-5M10 19a2 2 0 0 0 4 0',
};

function NavIcon({ path }: { path: string }): JSX.Element {
  return (
    <svg
      className="nav__icon"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={NAV_ICONS[path] ?? NAV_ICONS['/']} />
    </svg>
  );
}

const NAV_GROUPS: readonly NavGroup[] = [
  { titleKey: 'navAccounting', paths: ['/', '/accounts', '/reports/trial-balance', '/closing'] },
  { titleKey: 'navDocuments', paths: ['/documents', '/journal'] },
  { titleKey: 'navSettings', paths: ['/settings', '/notifications'] },
];

function Placeholder({ route }: { route: AppRoute }): JSX.Element {
  const { t } = useI18n();
  return (
    <section className="card" data-testid="screen-placeholder">
      <h2>{t(route.titleKey)}</h2>
      <p className="muted">{t('placeholderBody')}</p>
      <p className="muted">
        {t('placeholderRoute')}: <code>{route.path}</code>
      </p>
    </section>
  );
}

function ScreenOutlet({ route, params }: { route: AppRoute; params: Record<string, string> }): JSX.Element {
  const Screen = SCREENS[route.screen];
  if (Screen === undefined) {
    return <Placeholder route={route} />;
  }
  return <Screen params={params} />;
}

function NotFound(): JSX.Element {
  const { t } = useI18n();
  const { navigate } = useRouter();
  return (
    <section className="empty" data-testid="not-found">
      <h1>{t('notFoundTitle')}</h1>
      <p className="muted">{t('notFoundBody')}</p>
      <button type="button" className="btn btn--primary" onClick={() => navigate('/')}>
        {t('backHome')}
      </button>
    </section>
  );
}

function Forbidden(): JSX.Element {
  const { t } = useI18n();
  const { navigate } = useRouter();
  return (
    <section className="empty" data-testid="forbidden">
      <h1>{t('forbiddenTitle')}</h1>
      <p className="muted">{t('forbiddenBody')}</p>
      <button type="button" className="btn" onClick={() => navigate('/')}>
        {t('backHome')}
      </button>
    </section>
  );
}

export function AppShell({ session, onLogout }: { session: AuthSession; onLogout(): void }): JSX.Element {
  const { t, language, setLanguage } = useI18n();
  const { path, navigate } = useRouter();
  const effectivePath = path === '/login' ? '/' : path;
  const matched = matchRoute(effectivePath);
  const visible = NAV_ROUTES.filter((route) => can(session.user.permissions, route.permission));
  const allowed = matched === undefined || can(session.user.permissions, matched.route.permission);
  const navigationFor = (group: NavGroup) =>
    group.paths
      .map((target) => visible.find((route) => route.path === target))
      .filter((route): route is AppRoute => route !== undefined);
  const groupOfActive = NAV_GROUPS.find((group) => group.paths.includes(matched?.route.path ?? ''));

  return (
    <div className="app-shell">
      <aside className="side">
        <div className="side__brand">
          <span className="side__logo" aria-hidden="true">
            {t('appLogo')}
          </span>
          <span>
            <b>{t('appName')}</b>
            <small>{t('sideTagline')}</small>
          </span>
        </div>

        <div className="side__org">
          <b title={session.user.email}>{session.user.fullName}</b>
          <small>{session.user.email}</small>
        </div>

        <nav className="nav" aria-label={t('appName')}>
          {NAV_GROUPS.map((group) => {
            const items = navigationFor(group);
            if (items.length === 0) {
              return null;
            }
            return (
              <div className="nav__group" key={group.titleKey}>
                <span className="nav__label">{t(group.titleKey)}</span>
                {items.map((route) => {
                  const active = matched?.route.path === route.path;
                  return (
                    <button
                      key={route.path}
                      type="button"
                      className={active ? 'nav__item is-active' : 'nav__item'}
                      data-nav={route.path}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => navigate(route.path)}
                    >
                      <NavIcon path={route.path} />
                      {t(route.nav?.labelKey ?? route.titleKey)}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="side__foot">
          <span className="mono">{`${t('versionPrefix')}${APP_VERSION}`}</span>
          <span className="muted">{t('footerCopyright')}</span>
        </div>
      </aside>

      <div className="main">
        <header className="top">
          <div className="top__actions">
            <div className="lang-switch" role="group" aria-label={t('languageLabel')}>
              {LANGUAGES.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={`lang-switch__btn${code === language ? ' is-active' : ''}`}
                  aria-pressed={code === language}
                  data-lang={code}
                  onClick={() => setLanguage(code)}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
            <NotificationsBell />
            <span className="top__user" title={session.user.email}>
              {session.user.fullName}
            </span>
            <button type="button" className="btn btn--sm" data-testid="logout" onClick={onLogout}>
              {t('authLogout')}
            </button>
          </div>
        </header>

        <main className="app-main" data-route={matched?.route.path}>
          {matched === undefined ? (
            <NotFound />
          ) : !allowed ? (
            <Forbidden />
          ) : (
            <>
              <p className="crumbs">
                <span>{groupOfActive ? t(groupOfActive.titleKey) : t('appName')}</span>
                <span aria-hidden="true">/</span>
                <strong data-testid="screen-title">{t(matched.route.titleKey)}</strong>
              </p>
              <ScreenOutlet route={matched.route} params={matched.params} />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
